import { useState, useRef, useMemo, useCallback } from 'react'
import type { HotelSearchOffer, HotelMapViewData, ExperienceMapViewData } from 'tripPlanner/api/types'
import {
  transformOffersToMarkers,
  transformOffersToExperienceMarkers,
  type MapMarkerComponent,
} from 'tripPlanner/utils/mapMarkerTransforms'

type SearchStatus = 'idle' | 'success' | 'error'

interface HotelSearchState {
  status: SearchStatus
  searchQuery: string
  offers: Array<HotelSearchOffer>
  errorMessage?: string
}

interface UseSearchResultsReturn {
  /** Current search status */
  status: SearchStatus
  /** The NLP search query (used for carousel title and filter chip) */
  searchQuery: string
  /** The hotel offers from the search */
  offers: Array<HotelSearchOffer>
  /** Error message if search failed */
  errorMessage?: string
  /** Computed map markers from offers (memoized) */
  hotelMapMarkers: Array<MapMarkerComponent>
  /** Whether hotel filter is currently visible */
  isHotelFilterVisible: boolean
  /** Set success state with results (called when results arrive) */
  setHotelSearchResults: (data: HotelMapViewData & { hotelSearchCallId?: string }, generation: number) => void
  /** Set error state (called when search fails) */
  setHotelSearchError: (message: string, generation: number, hotelSearchCallId?: string) => void
  /** Populate state from carousel click (for historical carousels) */
  setHotelSearchResultsFromCarousel: (data: HotelMapViewData) => void
  /** Hide the hotel filter (called when filter chip is dismissed) */
  hideHotelFilter: () => void
  /** Reset hotel search state (called when starting a new chat) */
  resetHotelSearch: () => void
  /** The experience search query (used for filter chip label) */
  experienceSearchQuery: string
  /** Computed map markers from experience offers (memoized) */
  experienceMapMarkers: Array<MapMarkerComponent>
  /** Whether experience filter is currently visible */
  isExperienceFilterVisible: boolean
  /** Populate experience state from carousel "Map view" click */
  setExperienceSearchResultsFromCarousel: (data: ExperienceMapViewData) => void
  /** Hide the experience filter (called when filter chip is dismissed) */
  hideExperienceFilter: () => void
  /** Reset experience search state (called when starting a new chat) */
  resetExperienceSearch: () => void
}

/**
 * Hook to manage hotel search results state.
 * Handles success and error states with race condition protection.
 */
export function useSearchResults(): UseSearchResultsReturn {
  const [state, setState] = useState<HotelSearchState>({
    status: 'idle',
    searchQuery: '',
    offers: [],
  })

  const [isHotelFilterVisible, setIsHotelFilterVisible] = useState(false)

  // Generation counter for race condition handling
  const searchGenerationRef = useRef(0)
  const dismissedGenerationRef = useRef<number | null>(null)
  const dismissedSearchCallIdRef = useRef<string | null>(null)
  const activeSearchCallIdRef = useRef<string | null>(null)

  const shouldIgnoreUpdate = useCallback((generation: number, hotelSearchCallId?: string) => {
    if (hotelSearchCallId && dismissedSearchCallIdRef.current === hotelSearchCallId) {
      return true
    }
    if (!hotelSearchCallId && dismissedGenerationRef.current === generation) {
      return true
    }
    return generation < searchGenerationRef.current
  }, [])

  const setHotelSearchResults = useCallback((
    data: HotelMapViewData & { hotelSearchCallId?: string },
    generation: number,
  ) => {
    // Only reject if this is from an OLDER generation (prevents stale updates)
    // Accept newer generations and update our reference
    if (shouldIgnoreUpdate(generation, data.hotelSearchCallId)) {
      return
    }
    // Update our generation to match the incoming one
    searchGenerationRef.current = generation
    dismissedGenerationRef.current = null
    dismissedSearchCallIdRef.current = null
    activeSearchCallIdRef.current = data.hotelSearchCallId ?? null

    setState({
      status: 'success',
      searchQuery: data.searchQuery,
      offers: data.offers,
    })
    setIsHotelFilterVisible(data.offers.length > 0)
  }, [shouldIgnoreUpdate])

  const setHotelSearchError = useCallback((message: string, generation: number, hotelSearchCallId?: string) => {
    // Only reject if this is from an OLDER generation
    if (shouldIgnoreUpdate(generation, hotelSearchCallId)) return
    searchGenerationRef.current = generation
    dismissedGenerationRef.current = null
    dismissedSearchCallIdRef.current = null
    activeSearchCallIdRef.current = hotelSearchCallId ?? null

    setState(prev => ({
      ...prev,
      status: 'error',
      errorMessage: message,
    }))
    // Don't hide hotel filter — preserve previous results if any exist
  }, [shouldIgnoreUpdate])

  const setHotelSearchResultsFromCarousel = useCallback((
    data: HotelMapViewData,
  ) => {
    // This bypasses generation check as it's a direct user action (clicking "Map view")
    searchGenerationRef.current += 1
    dismissedGenerationRef.current = null
    dismissedSearchCallIdRef.current = null
    activeSearchCallIdRef.current = null
    setState({
      status: 'success',
      searchQuery: data.searchQuery,
      offers: data.offers,
    })
    setIsHotelFilterVisible(true)
  }, [])

  const hideHotelFilter = useCallback(() => {
    setIsHotelFilterVisible(false)
    dismissedGenerationRef.current = searchGenerationRef.current
    dismissedSearchCallIdRef.current = activeSearchCallIdRef.current
  }, [])

  const resetHotelSearch = useCallback(() => {
    setState({
      status: 'idle',
      searchQuery: '',
      offers: [],
    })
    setIsHotelFilterVisible(false)
    searchGenerationRef.current = 0
    dismissedGenerationRef.current = null
    dismissedSearchCallIdRef.current = null
    activeSearchCallIdRef.current = null
  }, [])

  const hotelMapMarkers = useMemo(() => {
    if (state.status === 'idle' || state.offers.length === 0) {
      return []
    }
    // Show markers for both 'success' and 'error' (preserves previous results on error)
    return transformOffersToMarkers(state.offers)
  }, [state.status, state.offers])

  const [experienceState, setExperienceState] = useState<HotelSearchState>({
    status: 'idle',
    searchQuery: '',
    offers: [],
  })

  const [isExperienceFilterVisible, setIsExperienceFilterVisible] = useState(false)

  const setExperienceSearchResultsFromCarousel = useCallback((data: ExperienceMapViewData) => {
    setExperienceState({
      status: 'success',
      searchQuery: data.searchQuery,
      offers: data.offers,
    })
    setIsExperienceFilterVisible(true)
  }, [])

  const hideExperienceFilter = useCallback(() => {
    setIsExperienceFilterVisible(false)
  }, [])

  const resetExperienceSearch = useCallback(() => {
    setExperienceState({
      status: 'idle',
      searchQuery: '',
      offers: [],
    })
    setIsExperienceFilterVisible(false)
  }, [])

  const experienceMapMarkers = useMemo(() => {
    if (experienceState.status === 'idle' || experienceState.offers.length === 0) {
      return []
    }
    return transformOffersToExperienceMarkers(experienceState.offers)
  }, [experienceState.status, experienceState.offers])

  return {
    status: state.status,
    searchQuery: state.searchQuery,
    offers: state.offers,
    errorMessage: state.errorMessage,
    hotelMapMarkers,
    isHotelFilterVisible,
    setHotelSearchResults,
    setHotelSearchError,
    setHotelSearchResultsFromCarousel,
    hideHotelFilter,
    resetHotelSearch,
    experienceSearchQuery: experienceState.searchQuery,
    experienceMapMarkers,
    isExperienceFilterVisible,
    setExperienceSearchResultsFromCarousel,
    hideExperienceFilter,
    resetExperienceSearch,
  }
}
